import {
    DocumentPart,
    PartType,
    PositionChangeProps,
    TagFiltered,
} from '@dv5/domain'
import React, {FC} from "react"
import {DocumentParamsEditor} from "../components/DocumentParamsEditor"
import {DocumentPartEditor} from "../components/DocumentPartEditor"
import {DocumentPartViewer} from "../components/DocumentPartViewer"
import {PartHeaderInfo} from "../components/PartHeaderInfo"
import {editMenuItems, viewMenuItems} from "./menuItems"
import {useDocumentPart} from "./useDocumentPart"
import {useComments} from "./useComments"
import {DocumentPartActions} from "./DocumentPartActions"
import {DocumentPartDialogs} from "./DocumentPartDialogs"
import './DocumentPartView.less'

interface Props {
    documentId: string
    part: DocumentPart
    arrangeProps: PositionChangeProps
    layers: Array<string>
    tags: Array<string>
    allTags: Array<TagFiltered>
    onSwap: (index: number, down: boolean) => void
    onClone: (documentId: string, partId: string) => void
}

export const DocumentPartView: FC<Props> = ({
                                                documentId,
                                                part,
                                                layers,
                                                tags,
                                                allTags,
                                                arrangeProps,
                                                onSwap,
                                                onClone,
                                            }) => {
    const {
        setShowAddCommentDialog,
        loadComments,
        editComment,
        sendComment,
        deleteComment,
        partComments,
        showAddCommentDialog,
    } = useComments({documentId, partId: part.id})

    const {
        editMode,
        content,
        setContent,
        saveStatus,
        lastSavedTime,
        toggleEditMode,
        cancelEdit,
        handleManualSave,
        changeHeader,
        changeType,
        convertType,
        showChangeTypeDialog,
        setShowChangeTypeDialog,
        changeOnView,
        copyContent,
        handleMenuAction,
        removePart,
        handleSaveAsTemplate,
        handleChangeDocument,
        documents,
        showDeleteConfirmation,
        setShowDeleteConfirmation,
        showSaveTemplateDialog,
        setShowSaveTemplateDialog,
        showChangeDocumentDialog,
        setShowChangeDocumentDialog,
    } = useDocumentPart({
        documentId,
        part,
        layers,
        tags,
        onClone,
        onAddComment: () => setShowAddCommentDialog(true),
    })

    const hasContent = part.partContent && part.partContent.trim() !== ''

    const allowedTypes = !hasContent
        ? undefined
        : (part.partType === PartType.MARKDOWN || part.partType === PartType.PLAINTEXT)
            ? [PartType.MARKDOWN, PartType.PLAINTEXT]
            : [part.partType]

    return (
        <div className='documentPart'>
            <DocumentPartActions
                editMode={editMode}
                mode={arrangeProps.mode}
                part={part}
                documentId={documentId}
                arrangeProps={arrangeProps}
                onSwap={onSwap}
                onMenuAction={handleMenuAction}
                editMenuItems={editMenuItems}
                viewMenuItems={viewMenuItems}
            />
            <div className='documentPartContent'>
                {!editMode && <PartHeaderInfo
                    header={part.partHeader}
                    type={part.partType}
                    onCopy={copyContent}
                />}
                {!editMode && <DocumentPartViewer part={part} onChange={changeOnView}/>}
                {editMode &&
                    <DocumentParamsEditor part={part}
                                          documentId={documentId}
                                          tags={tags}
                                          allTags={allTags}
                                          onHeaderChange={changeHeader}
                                          onTypeChange={changeType}
                                          onCancel={toggleEditMode}
                                          allowedTypes={allowedTypes}
                    />}
                {editMode && (
                    <DocumentPartEditor
                        part={part}
                        partType={part.partType}
                        content={content}
                        setContent={setContent}
                        saveStatus={saveStatus}
                        lastSavedTime={lastSavedTime}
                        handleManualSave={handleManualSave}
                        cancelEdit={cancelEdit}/>
                )}
            </div>
            <DocumentPartDialogs
                part={part}
                showChangeTypeDialog={showChangeTypeDialog}
                onChangeType={convertType}
                onCancelChangeType={() => setShowChangeTypeDialog(false)}
                showDeleteConfirmation={showDeleteConfirmation}
                onConfirmDelete={removePart}
                onCancelDelete={() => setShowDeleteConfirmation(false)}
                showSaveTemplateDialog={showSaveTemplateDialog}
                onSaveTemplate={handleSaveAsTemplate}
                onCancelSaveTemplate={() => setShowSaveTemplateDialog(false)}
                showChangeDocumentDialog={showChangeDocumentDialog}
                currentDocumentId={documentId}
                documents={documents}
                onChangeDocument={handleChangeDocument}
                onCancelChangeDocument={() => setShowChangeDocumentDialog(false)}
                showAddCommentDialog={showAddCommentDialog}
                comments={partComments[part.id] ?? []}
                onEditComment={editComment}
                onGetComment={loadComments}
                onSendComment={sendComment}
                onDeleteComment={deleteComment}
                onCloseComment={() => setShowAddCommentDialog(false)}
            />
        </div>
    )
}
